let activeChatInterceptorsCount = 0;
let isFetchPatched = false;
let originalNativeFetch = null;

const setupFetchInterceptor = () => {
  if (isFetchPatched || typeof window === "undefined" || !window.fetch) {
    return;
  }
  isFetchPatched = true;
  originalNativeFetch = window.fetch;

  window.fetch = async (resource, config) => {
    const url =
      resource && typeof resource === "object" && "url" in resource
        ? resource.url
        : resource;

    const isChatRequest =
      activeChatInterceptorsCount > 0 &&
      url &&
      typeof url === "string" &&
      url.includes("/api/v1/answer-engine/chat-request");

    if (!isChatRequest) {
      return originalNativeFetch(resource, config);
    }

    let requestPayload = {};
    const questionTimestamp = new Date().toISOString();

    try {
      const isRequestObject =
        typeof resource === "object" && resource !== null && "url" in resource;
      if (isRequestObject && typeof resource.clone === "function") {
        const clonedReq = resource.clone();
        requestPayload = await clonedReq.json();
      } else if (config && config.body) {
        if (typeof config.body === "string") {
          requestPayload = JSON.parse(config.body);
        } else if (config.body instanceof Blob) {
          const text = await config.body.text();
          requestPayload = JSON.parse(text);
        }
      }
    } catch (e) {
      // Ignore JSON parse errors
    }

    let response;
    try {
      response = await originalNativeFetch(resource, config);
    } catch (err) {
      const answerTimestamp = new Date().toISOString();
      window.dispatchEvent(
        new CustomEvent("ae-widget:chat-request-completed", {
          detail: {
            requestPayload,
            responseData: {
              success: false,
              error: err.message || "Network error",
            },
            questionTimestamp,
            answerTimestamp,
          },
        })
      );
      throw err;
    }

    if (response) {
      const answerTimestamp = new Date().toISOString();
      let responseData = null;

      try {
        const clonedResponse = response.clone();
        responseData = await clonedResponse.json();

        if (!response.ok) {
          if (responseData && typeof responseData === "object") {
            responseData.success = false;
          } else {
            responseData = {
              success: false,
              error:
                typeof responseData === "string"
                  ? responseData
                  : `HTTP ${response.status}: ${response.statusText}`,
              status: response.status,
              statusText: response.statusText,
            };
          }
        }
      } catch (e) {
        let errorText = "";
        try {
          const clonedResponseText = response.clone();
          errorText = await clonedResponseText.text();
        } catch (textErr) {
          // Ignore error reading response text
        }

        responseData = {
          success: false,
          error:
            errorText ||
            e.message ||
            `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
          statusText: response.statusText,
        };
      }

      if (responseData) {
        window.dispatchEvent(
          new CustomEvent("ae-widget:chat-request-completed", {
            detail: {
              requestPayload,
              responseData,
              questionTimestamp,
              answerTimestamp,
            },
          })
        );
      }
    }

    return response;
  };
};

/**
 * Register active interception for widget chat requests while a Sandbox instance is mounted.
 * Returns an unregister function. Does not unpatch or swap window.fetch on unmount,
 * completely preventing race conditions and stale closures.
 */
export const registerWidgetChatInterceptor = () => {
  setupFetchInterceptor();
  activeChatInterceptorsCount++;

  return () => {
    activeChatInterceptorsCount = Math.max(0, activeChatInterceptorsCount - 1);
  };
};

/**
 * Test-only utility to reset interceptor state between tests.
 */
export const __resetFetchInterceptorForTesting = () => {
  if (isFetchPatched && originalNativeFetch && typeof window !== "undefined") {
    window.fetch = originalNativeFetch;
  }
  activeChatInterceptorsCount = 0;
  isFetchPatched = false;
  originalNativeFetch = null;
};
